import React, { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ColumnPanel } from '@cld/column-panel';
import { IconProps } from '@cld/icon';
import { RecipeComplexity, CookbookFilterConfig } from '../../types';
import { usePlatformAnalytics } from '../../utils/hooks/useAnalytics/useAnalytics';
import { CategoryOption, ComplexityOption, isCategoryOption, isComplexityOption, SearchQuery } from '../SearchField/SearchField.helper';
import { messages } from '../../i18n';

export interface CookbookFilterPanelProps {
  query: SearchQuery;
  setQuery: (sq: SearchQuery) => void;
  filtersConfig?: CookbookFilterConfig;
}

export const CookbookFilterPanel: FC<React.PropsWithChildren<CookbookFilterPanelProps>> = ({ query, setQuery, filtersConfig, ...restProps }) => {
  const { formatMessage } = useIntl();
  const analytics = usePlatformAnalytics('cookbook search bar');

  const complexityOptions = useMemo(() => query.filter(isComplexityOption), [query]);
  const categoryOptions = useMemo(() => query.filter(isCategoryOption), [query]);

  const getComplexityRecord = useCallback(
    (complexity: RecipeComplexity, icon: IconProps['name'], newValue: ComplexityOption) => {
      const option = complexityOptions.find((option) => option.complexity === complexity);
      const onChange = () => {
        setQuery(option ? query.filter((queryOption) => queryOption !== option) : [...query, newValue]);
        if (!option) {
          analytics('complexity', newValue.complexity);
        }
      };

      return (
        <ColumnPanel.Record
          id={`filter-panel-complexity-${complexity}-checkbox`}
          count={filtersConfig?.complexity[complexity].recipesCount}
          value={!!option}
          onChange={onChange}
          icon={icon}
        >
          {formatMessage(messages[complexity])}
        </ColumnPanel.Record>
      );
    },
    [analytics, complexityOptions, filtersConfig, formatMessage, query, setQuery]
  );

  const getCategoryRecord = useCallback(
    (category: string, newValue: CategoryOption, count: number) => {
      const option = categoryOptions.find((option) => option.category === category);
      const onChange = () => {
        if (!option) {
          analytics('category', newValue.category.toLowerCase());
        }
        setQuery(option ? query.filter((queryOption) => queryOption !== option) : [...query, newValue]);
      };
      return (
        <ColumnPanel.Record key={category} id={`filter-panel-category-${category}-checkbox`} count={count} value={!!option} onChange={onChange}>
          {category}
        </ColumnPanel.Record>
      );
    },
    [analytics, categoryOptions, query, setQuery]
  );

  const beginner = useMemo(() => getComplexityRecord('beginner', 'complexityBeginner', { complexity: 'beginner' }), [getComplexityRecord]);
  const intermediate = useMemo(() => getComplexityRecord('intermediate', 'complexityIntermediate', { complexity: 'intermediate' }), [getComplexityRecord]);
  const advanced = useMemo(() => getComplexityRecord('advanced', 'complexityAdvanced', { complexity: 'advanced' }), [getComplexityRecord]);

  const categories = useMemo(
    () =>
      filtersConfig?.categories
        .filter((categoryInfo) => categoryInfo.recipesCount > 0)
        .map((categoryInfo) => getCategoryRecord(categoryInfo.name, { category: categoryInfo.name }, categoryInfo.recipesCount)),
    [filtersConfig, getCategoryRecord]
  );

  return (
    <ColumnPanel size="md" {...restProps}>
      <ColumnPanel.Section name={formatMessage(messages.filterPanelComplexitiesLabel)}>
        {beginner}
        {intermediate}
        {advanced}
      </ColumnPanel.Section>
      <ColumnPanel.Section
        columnWidth={164}
        onShowMore={() => analytics('category', 'show-more')}
        showGradually
        name={formatMessage(messages.filterPanelCategoriesLabel)}
      >
        {categories}
      </ColumnPanel.Section>
    </ColumnPanel>
  );
};
